import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 13 — Notification/Outbox and FeatureOnboardingState.
 * Mirrors feedbackQueries.js's `request()` error contract exactly
 * (`.status`/`.body` on thrown errors).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export function useNotifications(workspaceId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["notifications", workspaceId],
    enabled: !!workspaceId && enabled,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/notifications`),
    refetchInterval: 20000,
  });
}

export function useNotificationUnreadCount(workspaceId) {
  return useQuery({
    queryKey: ["notification-unread-count", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/notifications/unread-count`),
    refetchInterval: 20000,
  });
}

export function useOpenNotification(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ntfId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/notifications/${ntfId}/open`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications", workspaceId] });
      qc.invalidateQueries({ queryKey: ["notification-unread-count", workspaceId] });
    },
  });
}

export function useOnboardingState(workspaceId, featureKey) {
  return useQuery({
    queryKey: ["onboarding-state", workspaceId, featureKey],
    enabled: !!workspaceId && !!featureKey,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/onboarding/${featureKey}`),
  });
}

export function useCompleteOnboarding(workspaceId, featureKey) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skipped) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/onboarding/${featureKey}/complete?skipped=${!!skipped}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["onboarding-state", workspaceId, featureKey] }),
  });
}

export function useResetOnboarding(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (featureKey) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/onboarding/${featureKey}/reset`),
    onSuccess: (_, featureKey) => qc.invalidateQueries({ queryKey: ["onboarding-state", workspaceId, featureKey] }),
  });
}
